import { __decorate, __metadata } from "tslib";
import { ApplicationRef, DOCUMENT } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { takeWhileAlive } from '@core/frontend-shared';
import { ConfigService } from '@core/shared/config';
import { Action, Selector, State, Store } from '@ngxs/store';
import { first, interval, merge, race, timer } from 'rxjs';
import { VERSIONCHECK_PROTOCOL } from '../token';
import { ProtocolDistributor } from '@core/shared/protocol';
import * as i0 from "@angular/core";
import * as i1 from "@core/shared/config";
import * as i2 from "@ngxs/store";
import * as i3 from "@angular/service-worker";
import * as i4 from "@core/shared/protocol";
// Interesting other concept: https://blog.nodeswat.com/automagic-reload-for-clients-after-deploy-with-angular-4-8440c9fdd96c
export const VersioncheckActions = {
  FetchVersion: class {
    static {
      this.type = '[Versioncheck] FetchVersion';
    }
    constructor() {}
  },
  CheckForUpdate: class {
    static {
      this.type = '[Versioncheck] CheckForUpdate';
    }
    constructor() {}
  },
  UpdateAvailable: class {
    static {
      this.type = '[Versioncheck] UpdateAvailable';
    }
    constructor() {}
  },
  ActivateUpdate: class {
    static {
      this.type = '[Versioncheck] ActivateUpdate';
    }
    constructor() {}
  },
  PostponeUpdate: class {
    static {
      this.type = '[Versioncheck] PostponeUpdate';
    }
    constructor() {}
  }
};
let VersioncheckState = /*#__PURE__*/(() => {
  let VersioncheckState = class VersioncheckState {
    static newVersionAvailable(state) {
      return state.newVersionAvailable;
    }
    constructor(config, appRef, store, document,
    // optional because apps like sandbox do not configure service worker/updates.
    // in case SwUpdate is not available, update polling will be disabled.
    updates, protocol) {
      this.config = config;
      this.appRef = appRef;
      this.document = document;
      this.updates = updates;
      this.protocol = protocol;
      this.refreshIntervalSeconds = this.config.get('versioncheck.refreshinterval', 0.25 * 60);
      this.checkForUpdate$ = merge(race(
      // isStable never becomes true, at least in dev env. 
      // Due to that, a race with timer was added to ensure an update check will be run soon after booting.
      this.appRef.isStable.pipe(first(isStable => isStable === true)), timer(4000)), interval(this.refreshIntervalSeconds * 1000));
      this.versionInfo = config.get('versioninfo');
      const isAdminApp = config.isPlatform('admin');
      this.appBaseUrl = config.buildPlatformUrl(isAdminApp ? 'admin' : 'frontend');
      // console.log('retrieved versioninfo',this.versionInfo)
      // updates.isEnabled will only be true in production (see provider in app.module.ts)
      // however, we mimic as much of the implementation in dev env as possible to allow for testing.
      // only the actual call of SwUpdate.checkForUpdate will be skipped. 
      // inspect VERSIONCHECK_PROTOCOL to see details.
      // 
      // NOTE: NGSW Troubleshooting
      // ngsw is being loaded in dev mode too. This can cause long, seemingly endless page reload loops.
      // when having reload loop issue, manually clear all service workers in devtools application page.
      // Issue tracked in https://github.com/angular/angular/issues/47455
      if (updates) {
        // these will only emit in production (where updates.isEnabled is true)
        updates.versionUpdates.pipe(takeWhileAlive()).subscribe(event => {
          // since ng14, versionUpdates will emit even if no version is available! #2znmc58
          this.protocol.write('update check completed. Result: ' + event.type, event);
          if (event.type === 'NO_NEW_VERSION_DETECTED') return;
          if (event.type === 'VERSION_INSTALLATION_FAILED') return;
          store.dispatch(new VersioncheckActions.UpdateAvailable());
        });
        updates.unrecoverable.pipe(takeWhileAlive()).subscribe(err => {
          this.protocol.write('SwUpdate discovered unrecoverable state! Reload is recommended!', err);
        });
        if (updates.isEnabled) {
          this.protocol.write('Update querying enabled, check for new version every ' + this.refreshIntervalSeconds + 's');
        } else {
          this.protocol.write('Update querying disabled, NGSW not found or disabled.');
        }
        this.checkForUpdate$.pipe(takeWhileAlive()).subscribe(() => {
          store.dispatch(new VersioncheckActions.CheckForUpdate());
        });
      } else {
        this.protocol.write('No provider for SwUpdate, NGSW seems not to be part of application.');
      }
    }
    checkForUpdate(ctx, action) {
      const postponed = ctx.getState().postponedUntil;
      const ngswDisabled = !this.updates?.isEnabled;
      const updatePostponed = postponed && postponed > Date.now();
      this.protocol.write(`checkForUpdate -- ngswDisabled=${ngswDisabled}, updatePostponed=${updatePostponed}`);
      if (ngswDisabled || updatePostponed) return;
      this.updates.checkForUpdate();
    }
    updateAvailable(ctx, action) {
      const postponed = ctx.getState().postponedUntil;
      const ngswUnavailable = !this.updates;
      const updatePostponed = postponed && postponed > Date.now();
      this.protocol.write(`update available! -- ngswUnavailable=${ngswUnavailable}, updatePostponed=${updatePostponed}`);
      if (ngswUnavailable || updatePostponed) return;
      ctx.patchState({
        newVersionAvailable: true
      });
    }
    activateUpdate(ctx, action) {
      this.protocol.write(`activateUpdate`);
      this.updates.activateUpdate().then(success => {
        this.document.location.reload();
      });
    }
    postponeUpdate(ctx, action) {
      this.protocol.write(`postponeUpdate`);
      ctx.patchState({
        newVersionAvailable: false,
        postponedUntil: Date.now() + 60 * 60 * 1000 // 1h in ms
      });
    }
    static {
      this.ɵfac = function VersioncheckState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || VersioncheckState)(i0.ɵɵinject(i1.ConfigService), i0.ɵɵinject(i0.ApplicationRef), i0.ɵɵinject(i2.Store), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(i3.SwUpdate, 8), i0.ɵɵinject(VERSIONCHECK_PROTOCOL));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: VersioncheckState,
        factory: VersioncheckState.ɵfac
      });
    }
  };
  __decorate([Action(VersioncheckActions.CheckForUpdate), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], VersioncheckState.prototype, "checkForUpdate", null);
  __decorate([Action(VersioncheckActions.UpdateAvailable), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], VersioncheckState.prototype, "updateAvailable", null);
  __decorate([Action(VersioncheckActions.ActivateUpdate), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], VersioncheckState.prototype, "activateUpdate", null);
  __decorate([Action(VersioncheckActions.PostponeUpdate), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, void 0]), __metadata("design:returntype", void 0)], VersioncheckState.prototype, "postponeUpdate", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], VersioncheckState, "newVersionAvailable", null);
  VersioncheckState = __decorate([State({
    name: 'versioncheck',
    defaults: {
      aborted: false,
      newVersionAvailable: false,
      postponedUntil: 0
    }
  }), __metadata("design:paramtypes", [ConfigService, ApplicationRef, Store, Document, SwUpdate, ProtocolDistributor])], VersioncheckState);
  return VersioncheckState;
})();
export { VersioncheckState };